/**
 * Exchange-bezogene Socket Handler
 *
 * Factory-Funktionen für Kartentausch-Events.
 *
 * NEW: Exchange is now a sub-phase of 'playing', not a separate phase.
 * The server sends exchangeState with the game state.
 */

import { useCallback } from 'react';
import soundService from '../../soundService';
import musicService from '../../musicService';

/**
 * Erstellt Exchange-bezogene Event-Handler
 */
export function useExchangeHandlers(config) {
  const {
    dispatch,
    players,
    setExchangeInfo,
    setExchangeReady,
    setExchangeCurrentPlayer,
    setCardsPerPlayer,
    setExchangeCardsDealt,
    setSealedCards,
    setShowShop,
    setShowExchange,
    setGamePhase,
    // NEW: Notification callback for toasts
    notify,
  } = config;

  /**
   * Handler für exchangePhaseStarted Event
   * NEW: Sets up exchange as sub-phase of 'playing'
   */
  const handleExchangePhaseStarted = useCallback((data) => {
    // Play shuffle sound when card count selection starts
    if (!data.cardsDealt) {
      soundService.play('card_shuffle');
    } else {
      soundService.play('exchange_phase');
    }
    // NOTE: We intentionally do NOT set globalState.rankings here!
    // data.rankings contains the rankings from the PREVIOUS round, used only for exchange logic.
    // Setting it to globalState.rankings would incorrectly trigger isOnlyPlayerRemaining=true
    // and disable buttons for remaining players.
    // The exchangeInfo in exchangeState already contains the exchange roles based on previous rankings.

    // NEW: Set exchangeState directly
    if (data.exchangeState) {
      dispatch?.({ type: 'SET_EXCHANGE_STATE', payload: data.exchangeState });
      setExchangeInfo(data.exchangeState.exchangeInfo);
    } else if (data.exchangeInfo) {
      // Fallback for legacy format
      setExchangeInfo(data.exchangeInfo);
    }

    setExchangeReady?.(Array(players.length).fill(false));

    if (data.cardsPerPlayer) {
      setCardsPerPlayer(data.cardsPerPlayer);
    }
    if (data.exchangeCurrentPlayer !== undefined) {
      setExchangeCurrentPlayer?.(data.exchangeCurrentPlayer);
    }
    setExchangeCardsDealt(data.cardsDealt || false);

    // NEW: Stay in 'playing' phase, exchange is now a sub-phase
    setShowShop(false);
    setShowExchange(false); // No separate exchange screen
    setGamePhase?.('playing');
    dispatch?.({ type: 'SET_PHASE', payload: 'playing' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, players, setExchangeInfo, setCardsPerPlayer, setExchangeCardsDealt, setShowShop, setShowExchange]);

  /**
   * Handler für playerExchangeReady Event (legacy, kept for compatibility)
   */
  const handlePlayerExchangeReady = useCallback((data) => {
    setExchangeReady(prev => {
      const updated = [...prev];
      updated[data.playerIndex] = true;
      return updated;
    });
  }, [setExchangeReady]);

  /**
   * Handler für cardsPerPlayerChanged Event
   */
  const handleCardsPerPlayerChanged = useCallback((data) => {
    setCardsPerPlayer(data.cardsPerPlayer);
  }, [setCardsPerPlayer]);

  /**
   * NEW: Handler für exchangeCardsDealt Event (new event name)
   * Called when Asso confirms card count and cards are dealt
   */
  const handleExchangeCardsDealt = useCallback((data) => {
    // Note: card_deal sound is played by DealingAnimation component
    // Play music based on round number (3-track rotation):
    // roundNumber=0 (Partie 1) → main
    // roundNumber=1 (Partie 2) → vacation
    // roundNumber=2 (Partie 3) → amsterdam
    // roundNumber=3 (Partie 4) → main, etc.
    const roundNumber = data.roundNumber || 0;
    const tracks = ['main', 'vacation', 'amsterdam'];
    const track = tracks[roundNumber % 3];
    musicService.play(track);

    setExchangeCardsDealt(true);
    setCardsPerPlayer(data.cardsPerPlayer);

    if (data.sealedCards) {
      setSealedCards?.(data.sealedCards);
    }

    // Set the full exchangeState
    if (data.exchangeState) {
      dispatch?.({ type: 'SET_EXCHANGE_STATE', payload: data.exchangeState });
      setExchangeInfo(data.exchangeState.exchangeInfo);
    }

    // Set exchangeCurrentPlayer (critical for test mode - determines who acts)
    if (data.exchangeCurrentPlayer !== undefined) {
      setExchangeCurrentPlayer?.(data.exchangeCurrentPlayer);
      dispatch?.({ type: 'SET_EXCHANGE_CURRENT_PLAYER', payload: data.exchangeCurrentPlayer });
      // In test mode: Set view to the player who should act now
      // This ensures we switch from the last seal distribution player to the exchange player
      dispatch?.({ type: 'SET_TEST_MODE_VIEW_PLAYER', payload: data.exchangeCurrentPlayer });
    } else {
      // Clear testModeViewPlayer so exchange phase follows exchangeCurrentPlayer
      dispatch?.({ type: 'SET_TEST_MODE_VIEW_PLAYER', payload: null });
    }

  }, [dispatch, setExchangeCardsDealt, setCardsPerPlayer, setSealedCards, setExchangeInfo, setExchangeCurrentPlayer]);

  /**
   * Handler für cardsDealtForExchange Event (legacy)
   */
  const handleCardsDealtForExchange = useCallback((data) => {
    // Redirect to new handler
    handleExchangeCardsDealt(data);
  }, [handleExchangeCardsDealt]);

  /**
   * Handler für exchangePlayerChanged Event
   *
   * This event is ONLY used during exchange phase (giving/receiving cards).
   * Seal distribution player changes now use the dedicated sealDistributionPlayerChanged event.
   */
  const handleExchangePlayerChanged = useCallback((data) => {
    setExchangeCurrentPlayer(data.playerIndex);
    dispatch?.({ type: 'SET_EXCHANGE_CURRENT_PLAYER', payload: data.playerIndex });
    dispatch?.({ type: 'SET_TEST_MODE_VIEW_PLAYER', payload: data.playerIndex });
  }, [dispatch, setExchangeCurrentPlayer]);

  // Note: exchangeCardsFlying is now handled directly in PlayingPhase for animation

  /**
   * NEW: Handler für exchangePhaseChanged Event
   * Called when switching from 'giving' to 'receiving' phase
   */
  const handleExchangePhaseChanged = useCallback((data) => {
    if (data.exchangeState) {
      dispatch?.({ type: 'SET_EXCHANGE_STATE', payload: data.exchangeState });
    }

    // Update exchangeCurrentPlayer if provided (needed for local test mode)
    if (data.exchangeCurrentPlayer !== undefined) {
      dispatch?.({ type: 'SET_EXCHANGE_CURRENT_PLAYER', payload: data.exchangeCurrentPlayer });
      // Also update testModeViewPlayer so the view switches to the correct player
      dispatch?.({ type: 'SET_TEST_MODE_VIEW_PLAYER', payload: data.exchangeCurrentPlayer });
    }

    if (data.phase === 'receiving') {
      soundService.play('turn_notification');

      // Toast für erhaltene Karten (nur wenn Karten erhalten wurden)
      const receivedCards = data.exchangeState?.receivedCards;
      if (receivedCards && receivedCards.length > 0 && notify) {
        const count = receivedCards.length;
        notify(`+${count} Karte${count > 1 ? 'n' : ''} erhalten`, {
          icon: '🃏',
          priority: 'normal',
          duration: 3000
        });
      }
    }
  }, [dispatch, notify]);

  /**
   * NEW: Handler für exchangeComplete Event
   * Called when exchange is finished and normal playing begins
   */
  const handleExchangeComplete = useCallback((data) => {
    soundService.play('round_start');
    // Note: Music already switched to main in handleExchangeCardsDealt

    dispatch?.({ type: 'SET_EXCHANGE_STATE', payload: null });
    setExchangeCardsDealt(true);

    // Clear testModeViewPlayer so the view follows currentPlayer during gameplay
    dispatch?.({ type: 'SET_TEST_MODE_VIEW_PLAYER', payload: null });
  }, [dispatch, setExchangeCardsDealt]);

  // =============================================
  // SEAL DISTRIBUTION HANDLERS
  // =============================================

  /**
   * Handler für sealDistributionStarted Event
   * Called when seal distribution phase begins after dealing cards
   */
  const handleSealDistributionStarted = useCallback((data) => {
    soundService.play('seal_attach');
    dispatch?.({
      type: 'SEAL_DISTRIBUTION_STARTED',
      payload: {
        startTime: data.startTime,
        duration: data.duration,
        readyPlayers: data.readyPlayers || [],
        stash: data.stash,
        sealedCards: data.sealedCards,
      },
    });
  }, [dispatch]);

  /**
   * Handler für sealDistributionPlayerChanged Event (Test-Mode only)
   * Called when switching to the next player during seal distribution
   * This is separate from exchangePlayerChanged to avoid race conditions
   */
  const handleSealDistributionPlayerChanged = useCallback((data) => {
    soundService.play('turn_notification');

    // Update the current player for seal distribution
    dispatch?.({ type: 'SET_EXCHANGE_CURRENT_PLAYER', payload: data.playerIndex });

    // Update the view to show the new player
    dispatch?.({ type: 'SET_TEST_MODE_VIEW_PLAYER', payload: data.playerIndex });

    // Reset the timer for the new player
    if (data.startTime && data.duration) {
      dispatch?.({
        type: 'SEAL_DISTRIBUTION_TIMER_RESET',
        payload: {
          startTime: data.startTime,
          duration: data.duration,
        },
      });
    }

  }, [dispatch]);

  /**
   * Handler für sealPlaced Event (Bestätigung vom Server)
   */
  const handleSealPlaced = useCallback((data) => {
    soundService.play('seal_attach');
    dispatch?.({
      type: 'SEAL_PLACED',
      payload: { sealId: data.sealId, cardIndex: data.cardIndex },
    });
  }, [dispatch]);

  /**
   * Handler für sealRemoved Event
   */
  const handleSealRemoved = useCallback((data) => {
    dispatch?.({
      type: 'SEAL_REMOVED',
      payload: { sealId: data.sealId },
    });
  }, [dispatch]);

  /**
   * Handler für playerSealReady Event
   * Called when a player confirms their seal placements
   */
  const handlePlayerSealReady = useCallback((data) => {
    dispatch?.({
      type: 'PLAYER_SEAL_READY',
      payload: {
        playerIndex: data.playerIndex,
        readyCount: data.readyCount,
        totalPlayers: data.totalPlayers,
      },
    });
  }, [dispatch]);

  /**
   * Handler für sealDistributionComplete Event
   * Called when all players are done placing seals
   */
  const handleSealDistributionComplete = useCallback((data) => {
    soundService.play('round_start');
    dispatch?.({
      type: 'SEAL_DISTRIBUTION_COMPLETE',
      payload: {
        sealedCards: data.sealedCards,
        stash: data.stash,
      },
    });
  }, [dispatch]);

  /**
   * Handler für sealDistributionTimeout Event
   */
  const handleSealDistributionTimeout = useCallback(() => {
    soundService.play('timer_warning');
    dispatch?.({ type: 'SEAL_DISTRIBUTION_TIMEOUT', payload: {} });
  }, [dispatch]);

  /**
   * Handler für sealPlacementError Event
   */
  const handleSealPlacementError = useCallback((data) => {
    soundService.play('error');
    console.warn('[Exchange] Seal placement error:', data.error || 'Platzierung fehlgeschlagen');
  }, []);

  return {
    handleExchangePhaseStarted,
    handlePlayerExchangeReady,
    handleCardsPerPlayerChanged,
    handleCardsDealtForExchange,
    handleExchangeCardsDealt,
    handleExchangePlayerChanged,
    // Note: handleExchangeCardsFlying is now in PlayingPhase
    handleExchangePhaseChanged,
    handleExchangeComplete,
    // Seal Distribution
    handleSealDistributionStarted,
    handleSealDistributionPlayerChanged,
    handleSealPlaced,
    handleSealRemoved,
    handlePlayerSealReady,
    handleSealDistributionComplete,
    handleSealDistributionTimeout,
    handleSealPlacementError,
  };
}

export default useExchangeHandlers;
